import {inject, Injectable} from '@angular/core';
import {WebFormFieldSummary, WebFormSummary} from '../models/WebFormSummary';
import {CustomFormControl} from '../components/controls/CustomFormControl';
import {UntypedFormArray, UntypedFormGroup} from '@angular/forms';
import {FormPage} from '../models/WebFormTemplate';
import {FormHelperService} from './form-helper.service';

@Injectable({
  providedIn: 'root'
})
export class FormSummarizerService {

  private formHelper = inject(FormHelperService);

  constructor() { }

  getKeyWithId(id: string, index: number): string {
    return id.replace('{i}', '' + index);
  }

  buildWebFormSummaries(formPages: FormPage[], controls: Map<string, CustomFormControl>): WebFormSummary[] {
    const summaries: WebFormSummary[] = [];

    for (let i = 0; i < formPages.length; ++i) {
      const formPage = formPages[i];
      const formGroup = formPage.formGroup;

      if (!this.formHelper.isFormPageVisible(formPage, controls)) {
        continue;
      }

      const pageFieldsSummary: WebFormFieldSummary[] = [];

      Object.keys(formGroup.controls).forEach(key => {
        const formGroupControl = formGroup.controls[key];

        if (!(formGroupControl instanceof CustomFormControl)) {
          return;
        }
        switch (formGroupControl.element.type) {
          case 'grid':
            break; // Skip grids, let them be serialized by their individual controls
          case 'ssn':
            pageFieldsSummary.push(new WebFormFieldSummary(formGroupControl.element.label, 'XXX-XX-' + formGroupControl.value.slice(-4)));
            break;
          case 'file':
            let fileCount = 1;
            for (const file of formGroupControl.element.files) {
              pageFieldsSummary.push(new WebFormFieldSummary(`File #${fileCount}`, file.name));
              ++fileCount;
            }
            break;
          case 'template':
            this.extractTemplateFieldSummaries(formGroup, formGroupControl, pageFieldsSummary);
            break;
          case 'radio':
            formGroupControl.element.options.forEach(option => {
              if (option.id !== formGroupControl.value) { return; }
              pageFieldsSummary.push(new WebFormFieldSummary(formGroupControl.element.label, option.label));
            });

            break;
          case 'checkbox':
            if (key.startsWith('_')) { return; }
            const displayedValue = formGroupControl.value === true ? 'Yes' : 'No';
            pageFieldsSummary.push(new WebFormFieldSummary(formGroupControl.element.option, displayedValue));
            break;
          case 'number':
            if (key.startsWith('_')) { return; }
            const parsedValue = parseInt(formGroupControl.value, 10);
            const finalValue = isNaN(parsedValue) ? '' : parsedValue.toLocaleString('en-US');
            pageFieldsSummary.push(new WebFormFieldSummary(formGroupControl.element.label, finalValue));
            break;
          default:
            if (key.startsWith('_')) { return; }
            pageFieldsSummary.push(new WebFormFieldSummary(formGroupControl.element.label, formGroupControl.value));
            break;
        }
      });

      if (pageFieldsSummary.length === 0) {
        continue;
      }

      summaries.push(new WebFormSummary(formPages[i].title, formPages[i].disableModifyButton ?? false, i, pageFieldsSummary));
    }

    return summaries;
  }

  extractTemplateFieldSummaries(formGroup: UntypedFormGroup,
                                customControl: CustomFormControl,
                                pageFieldsSummary: WebFormFieldSummary[]): void {
    const formArray = formGroup.get(this.formHelper.getFormArrayName(customControl.element.id)) as UntypedFormArray;
    if (!formArray) {
      return;
    }

    let i = 0;
    for (const arrayControl of formArray.controls) {
      if (arrayControl instanceof UntypedFormGroup) {
        Object.keys(arrayControl.controls).forEach(key => {
          const arrayGroupControl = arrayControl.controls[key] as CustomFormControl;
          const keyWithId = this.getKeyWithId(arrayGroupControl.element.label, i + 1);
          pageFieldsSummary.push(new WebFormFieldSummary(keyWithId, arrayGroupControl.value));
        });
      }
      ++i;
    }
  }
}
